/**
 * Types du Dashboard
 *
 * DÃ©finitions des types pour les KPIs, alertes, anomalies et composants du dashboard.
 */

// ============================================================================
// TYPES KPIs
// ============================================================================

export interface DashboardStats {
  // Financiers
  cashBalance: number;
  totalDepositsToday: number;
  totalWithdrawalsToday: number;
  netFlowToday: number;

  // VolumÃ©trie
  transactionCountToday: number;
  depositCountToday: number;
  withdrawalCountToday: number;

  // Comparatifs
  transactionCountYesterday: number;
  netFlowYesterday: number;

  // Moyennes
  averageTransactionAmount: number;
  averageTransactionAmountLast7Days: number;

  // ExtrÃªmes
  largestWithdrawalToday: number;
  largestDepositToday: number;

  // Tiers
  thirdPartyDepositCount: number;
  thirdPartyDepositPercentage: number;
}

export interface HourlyVolume {
  hour: number; // 0-23
  deposits: number;
  withdrawals: number;
  transactionCount: number;
}

export interface ChannelDistribution {
  channel: 'guichet' | 'mobile' | 'web' | 'api';
  count: number;
  amount: number;
  percentage: number;
}

// ============================================================================
// TYPES ALERTES
// ============================================================================

export type AlertSeverity = 'critical' | 'high' | 'medium' | 'low';

export type AlertType =
  | 'high_amount'
  | 'unusual_pattern'
  | 'biometric_failure'
  | 'withdrawal_spike'
  | 'rapid_succession'
  | 'third_party_threshold'
  | 'balance_low'
  | 'system_sync'
  | 'expired_identity';

export interface Alert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  timestamp: Date;
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: Date;
  acknowledgeReason?: string;
  relatedTransactionId?: string;
  relatedAccountNumber?: string;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// TYPES ANOMALIES
// ============================================================================

export type AnomalyType =
  | 'amount_outlier'          // Montant > 3Ïƒ de l'historique
  | 'withdrawal_spike'        // Pic de retraits > 200% moyenne horaire
  | 'rapid_succession'        // > 3 transactions en 1h mÃªme compte
  | 'deposit_then_withdraw'   // Versement suivi de retrait < 5 min
  | 'biometric_failures'      // > 3 Ã©checs biomÃ©triques
  | 'round_amount'            // Montant rond exact (info)
  | 'high_third_party'        // > 30% versements par tiers
  | 'off_hours_activity'      // Transaction hors heures
  | 'balance_inconsistency';  // Solde calculÃ© â‰  affichÃ©

export interface Anomaly {
  id: string;
  type: AnomalyType;
  severity: AlertSeverity;
  detectedAt: Date;
  description: string;
  affectedAccount?: string;
  affectedTransaction?: string;
  value?: number;
  threshold?: number;
  recommendation: string;
  dismissed: boolean;
  dismissedBy?: string;
  dismissedAt?: Date;
  dismissReason?: string;
}

// ============================================================================
// TYPES STATUT SYSTÃˆME
// ============================================================================

export type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting';
export type SyncStatus = 'synced' | 'syncing' | 'error' | 'stale';

export interface SystemStatus {
  connectionStatus: ConnectionStatus;
  syncStatus: SyncStatus;
  lastSyncTime: Date;
  pendingTransactions: number;
  errorMessage?: string;
}

// ============================================================================
// TYPES TRANSACTIONS (pour le fil temps rÃ©el)
// ============================================================================

export interface TransactionSummary {
  id: string;
  type: 'DEPOSIT' | 'WITHDRAWAL';
  amount: number;
  accountNumber: string;
  clientName: string;
  timestamp: Date;
  reference: string;
  isThirdParty?: boolean;
  cashierId?: string;
  motif?: string;
  status?: 'completed' | 'pending' | 'cancelled';
  signaturesRequired?: number;
  signaturesCompleted?: number;
}

// ============================================================================
// TYPES FILTRES
// ============================================================================

export type DateRangePreset = 'today' | 'yesterday' | 'last7days' | 'last30days' | 'custom';

export interface DateRange {
  start: Date;
  end: Date;
  preset?: DateRangePreset;
}

export interface DashboardFilters {
  dateRange: DateRange;
  transactionTypes: ('DEPOSIT' | 'WITHDRAWAL')[];
  minAmount?: number;
  maxAmount?: number;
  showAnomaliesOnly: boolean;
}

// ============================================================================
// TYPES SEUILS DE CONFIGURATION
// ============================================================================

export interface DashboardThresholds {
  // Seuils de solde caisse
  cashBalanceWarning: number;  // 500 000 FCFA par dÃ©faut
  cashBalanceCritical: number; // 200 000 FCFA par dÃ©faut

  // Seuils de montant transaction
  highAmountThreshold: number; // 1 000 000 FCFA
  criticalAmountThreshold: number; // 2 000 000 FCFA

  // Seuils de volume
  hourlyVolumeSpike: number; // 150% de la moyenne
  rapidSuccessionMinutes: number; // 60 minutes
  rapidSuccessionCount: number; // 3 transactions

  // Seuils tiers
  thirdPartyWarningPercentage: number; // 30%

  // Timing
  depositWithdrawGapMinutes: number; // 5 minutes
}

export const DEFAULT_THRESHOLDS: DashboardThresholds = {
  cashBalanceWarning: 500000,
  cashBalanceCritical: 200000,
  highAmountThreshold: 1000000,
  criticalAmountThreshold: 2000000,
  hourlyVolumeSpike: 1.5,
  rapidSuccessionMinutes: 60,
  rapidSuccessionCount: 3,
  thirdPartyWarningPercentage: 30,
  depositWithdrawGapMinutes: 5,
};

export type ExternalServiceKey = 'ibank' | 'securgen' | 'internet';

export interface ExternalServiceStatus {
  key: ExternalServiceKey;
  label: string;
  isActive: boolean;
  source: 'api' | 'browser';
  lastChecked: Date;
  endpoint?: string;
  error?: string;
}
